import React, {useCallback, useMemo} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {useHistory, useParams} from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {Box, CircularProgress, Container, Theme} from '@mui/material'
import {getProductLineColors, PGTypography} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import CalendarEmbed from '@progyny/components/CalendarEmbed'
import JumboTitle from '@progyny/components/JumboTitle'
import {useConsultationsDetailQuery} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {ConsultationCard} from '@progyny/pages/Consultations/components/ConsultationCard'
import {showConsultationRescheduledToast} from '@progyny/pages/Consultations/components/ConsultationToast'
import {useMeStore} from '@progyny/store/meStore'
import {buildOnboardingMetadata} from '@progyny/utils/calendarMetadata'
import {shouldLockAttendeeFields} from '@progyny/utils/programLockAttendeeFields'
import {buildRoutingFormResponses} from '@progyny/utils/routingFormResponses'

interface OnboardingProps {
  /** Slug used to load onboarding / Cal.com config (e.g. `pregnancy-support` when overridden). */
  consultationSlug?: string
  /** Hub route slug for fallback when the primary slug has no `onboardingCalcomEventType` (e.g. `pregnancy`). */
  hubConsultationSlug?: string
  basePath?: string
}

const useStyles = makeStyles<{colors?: any}>()((theme: Theme, {colors}) => ({
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '64px',
    cursor: 'pointer',
    color: colors?.primary || '#148199',
    textDecoration: 'none',
    '&:hover': {
      opacity: 0.8,
    },
  },
  backText: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    color: colors?.primary || '#148199',
  },
}))

const Onboarding: React.FC<OnboardingProps> = ({
  consultationSlug: propSlug,
  hubConsultationSlug,
  basePath,
}) => {
  const {slug: paramSlug, mode} = useParams<{slug: string; mode?: string}>()
  const slug = propSlug || paramSlug
  const hubSlug =
    hubConsultationSlug ??
    (basePath?.split('/').filter(Boolean).pop() as string | undefined)
  const history = useHistory()
  const {t} = useTranslation()
  const user = useMeStore((s) => s.user)
  const countryCode = useMeStore((s) => s.countryCode)
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {classes} = useStyles({colors})

  const {
    data,
    loading: primaryLoading,
    refetch: refetchPrimary,
  } = useConsultationsDetailQuery({
    variables: {slug},
    skip: !slug,
  })

  const primaryConsultation = data?.consultations?.consultation
  const primaryHasCalcom =
    !!primaryConsultation?.onboardingCalcomEventType?.trim()

  const skipFallback = !hubSlug || hubSlug === slug || primaryHasCalcom

  const {
    data: fallbackData,
    loading: fallbackLoading,
    refetch: refetchFallback,
  } = useConsultationsDetailQuery({
    variables: {slug: hubSlug ?? ''},
    skip: skipFallback,
  })

  const consultation = useMemo(() => {
    const primary = data?.consultations?.consultation
    const fallback = fallbackData?.consultations?.consultation
    if (primary?.onboardingCalcomEventType?.trim()) return primary
    if (fallback?.onboardingCalcomEventType?.trim()) return fallback
    return primary ?? fallback
  }, [
    data?.consultations?.consultation,
    fallbackData?.consultations?.consultation,
  ])

  const calcomEventType = consultation?.onboardingCalcomEventType?.trim()
  const appointment = consultation?.myEnrollment?.onboardingCallAppointment
  const isRescheduleMode = mode === 'reschedule'
  const loading = primaryLoading || (!skipFallback && fallbackLoading)
  const isCalendarVisible = !loading && !!consultation && !!calcomEventType

  const handleBackClick = () => {
    if (basePath) {
      history.push(basePath)
    } else {
      history.push(`/consultations/${hubSlug || paramSlug || slug}`)
    }
  }

  const handleBookingSuccessful = useCallback(() => {
    const refetches = [refetchPrimary()]
    if (hubSlug && hubSlug !== slug && !skipFallback) {
      refetches.push(refetchFallback())
    }
    Promise.all(refetches).then(() => {
      if (isRescheduleMode) {
        showConsultationRescheduledToast()
      }
      if (basePath) {
        history.push(basePath)
      } else {
        history.push(`/consultations/${hubSlug || slug}`)
      }
    })
  }, [
    refetchPrimary,
    refetchFallback,
    skipFallback,
    hubSlug,
    slug,
    history,
    basePath,
    isRescheduleMode,
  ])

  return (
    <Container maxWidth={'xl'}>
      <JumboTitle
        title={
          isCalendarVisible
            ? undefined
            : isRescheduleMode
              ? t('consultations_onboarding_reschedule_your_onboarding_call')
              : t('consultations_onboarding_schedule_your_onboarding_call')
        }
        description={
          isCalendarVisible
            ? undefined
            : isRescheduleMode
              ? t('consultations_onboarding_select_new_time_for_your')
              : t('consultations_onboarding_book_introductory_call_with_our')
        }
        disableContentWrapper
        forceDefaultHeight={isCalendarVisible}
      >
        {!isCalendarVisible && (
          <Box pb={10}>
            <Box className={classes.backLink} onClick={handleBackClick}>
              <ArrowBackIcon
                sx={{fontSize: '20px', color: colors?.primary || '#148199'}}
              />
              <PGTypography className={classes.backText}>
                <Trans i18nKey='consultations_onboarding_back_to_overview' />
              </PGTypography>
            </Box>
            <ConsultationCard padding='md' borderRadius='lg'>
              {loading ? (
                <CircularProgress size={40} sx={{color: colors?.primary}} />
              ) : (
                <PGTypography>
                  <Trans i18nKey='consultations_onboarding_schedule_load_error' />
                </PGTypography>
              )}
            </ConsultationCard>
          </Box>
        )}
        {!loading && consultation && calcomEventType && (
          <ConsultationCard padding='none' borderRadius='lg'>
            <CalendarEmbed
              calLink={calcomEventType}
              rescheduleUid={
                isRescheduleMode
                  ? appointment?.calcomBookingUid || undefined
                  : undefined
              }
              title={
                isRescheduleMode
                  ? t(
                      'consultations_onboarding_reschedule_your_onboarding_call'
                    )
                  : t('consultations_onboarding_schedule_your_onboarding_call')
              }
              description={
                isRescheduleMode
                  ? t('consultations_onboarding_select_new_time_for_your')
                  : t(
                      'consultations_onboarding_book_introductory_call_with_our'
                    )
              }
              onBookingSuccessful={handleBookingSuccessful}
              prefillData={{
                email: user?.email || '',
                name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
              }}
              metadata={buildOnboardingMetadata(
                user?.id || '',
                consultation?.id
              )}
              lockAttendeeFields={shouldLockAttendeeFields(consultation?.slug)}
              routingFormResponses={buildRoutingFormResponses(countryCode)}
              onBack={handleBackClick}
            />
          </ConsultationCard>
        )}
      </JumboTitle>
    </Container>
  )
}

export default Onboarding
